<template>
  <el-dialog
    :visible.sync="visibleSync"
    width="480px"
    append-to-body
    :close-on-click-modal="!confirming"
    :close-on-press-escape="!confirming"
    :show-close="!confirming"
    custom-class="live-editor-op-dialog"
    :before-close="beforeClose"
    @closed="onClosed"
  >
    <template #title>
      <div class="live-editor-op-dialog__head">
        <span class="live-editor-op-dialog__title">{{ dialogTitle }}</span>
        <span v-if="modeTag" class="live-editor-op-dialog__tag">{{ modeTag }}</span>
      </div>
    </template>

    <div v-loading="confirming" class="live-editor-op-dialog__shell">
      <section class="live-editor-op-dialog__block">
        <header class="live-editor-op-dialog__block-head">
          <span class="live-editor-op-dialog__block-mark" aria-hidden="true" />
          <h4 class="live-editor-op-dialog__block-title">基础信息</h4>
        </header>
        <div class="live-editor-op-dialog__panel live-editor-op-dialog__panel--meta">
          <div class="live-editor-op-dialog__meta-grid">
            <div class="live-editor-op-dialog__meta-item">
              <span class="live-editor-op-dialog__meta-label">投诉日期</span>
              <span class="live-editor-op-dialog__meta-value">{{ displayMeta.date }}</span>
            </div>
            <div class="live-editor-op-dialog__meta-item">
              <span class="live-editor-op-dialog__meta-label">生产部门</span>
              <span class="live-editor-op-dialog__meta-value">{{ displayMeta.deptP }}</span>
            </div>
            <div class="live-editor-op-dialog__meta-item">
              <span class="live-editor-op-dialog__meta-label">异常投诉</span>
              <span class="live-editor-op-dialog__meta-value">{{ displayMeta.yc }}</span>
            </div>
            <div class="live-editor-op-dialog__meta-item">
              <span class="live-editor-op-dialog__meta-label">负责人</span>
              <span class="live-editor-op-dialog__meta-value">{{ displayMeta.fzr }}</span>
            </div>
            <div v-if="displayMeta.part" class="live-editor-op-dialog__meta-item">
              <span class="live-editor-op-dialog__meta-label">零件</span>
              <span class="live-editor-op-dialog__meta-value">{{ displayMeta.part }}</span>
            </div>
          </div>
        </div>
      </section>

      <section class="live-editor-op-dialog__block">
        <header class="live-editor-op-dialog__block-head">
          <span class="live-editor-op-dialog__block-mark" aria-hidden="true" />
          <h4 class="live-editor-op-dialog__block-title">处理信息</h4>
        </header>
        <div class="live-editor-op-dialog__panel live-editor-op-dialog__panel--form">
        <el-form label-width="96px" size="small" class="live-editor-op-dialog__form">
          <el-form-item label="计划日">
            <el-date-picker
              v-model="form.stagePlanDate"
              type="date"
              value-format="yyyy-MM-dd"
              :append-to-body="true"
              placeholder="选择计划日"
              class="live-editor-op-dialog__date"
              :disabled="readonly || planReadonly || confirming"
              clearable
            />
            <button
              v-if="!readonly && !planReadonly && !confirming && planDefault"
              type="button"
              class="live-editor-op-dialog__default"
              @click="form.stagePlanDate = planDefault"
            >
              恢复默认 {{ planDefault }}
            </button>
          </el-form-item>
          <template v-if="mode === 'reply'">
            <el-form-item label="回复实际日">
              <el-date-picker
                v-model="form.replyActual"
                type="date"
                value-format="yyyy-MM-dd"
                :append-to-body="true"
                placeholder="选择日期"
                class="live-editor-op-dialog__date"
                :disabled="readonly || confirming"
                clearable
              />
            </el-form-item>
            <el-form-item label="原因">
              <el-input
                v-model="form.cause"
                type="textarea"
                :rows="2"
                :disabled="readonly || confirming"
                placeholder="原因分析"
              />
            </el-form-item>
            <el-form-item label="临时对策">
              <el-input
                v-model="form.temp"
                type="textarea"
                :rows="2"
                :disabled="readonly || confirming"
                placeholder="临时对策"
              />
            </el-form-item>
          </template>
          <template v-else-if="mode === 'defect'">
            <el-form-item label="完成日">
              <el-date-picker
                v-model="form.defectActual"
                type="date"
                value-format="yyyy-MM-dd"
                :append-to-body="true"
                placeholder="选择日期"
                class="live-editor-op-dialog__date"
                :disabled="readonly || confirming"
                clearable
              />
            </el-form-item>
          </template>
          <template v-else-if="mode === 'close'">
            <el-form-item label="结案日">
              <span class="live-editor-op-dialog__readonly">{{ form.closeActual || '—' }}</span>
            </el-form-item>
          </template>
        </el-form>
        </div>
      </section>
    </div>

    <template #footer>
      <div class="live-editor-op-dialog__foot">
        <div class="live-editor-op-dialog__foot-left">
          <el-button
            v-if="showCloseCase"
            type="warning"
            plain
            size="small"
            :disabled="confirming"
            @click="$emit('close-case')"
          >
            今日结案
          </el-button>
        </div>
        <div class="live-editor-op-dialog__foot-right">
          <el-button :disabled="confirming" @click="onCancel">取消</el-button>
          <el-button
            type="primary"
            :loading="confirming"
            :disabled="confirmDisabled"
            @click="onConfirm"
          >
            确定
          </el-button>
        </div>
      </div>
    </template>
  </el-dialog>
</template>

<script lang="ts">
import Vue from 'vue';

export type LiveEditorOpDialogMode = 'reply' | 'defect' | 'close';

export interface LiveEditorOpDialogForm {
  stagePlanDate: string | null;
  replyActual: string | null;
  cause: string;
  temp: string;
  defectActual: string | null;
  closeActual: string | null;
}

export interface LiveEditorOpDialogMeta {
  date?: string;
  yc?: string;
  deptP?: string;
  fzr?: string;
  pm?: string | null;
  lh?: string | null;
}

function cloneForm(source: LiveEditorOpDialogForm): LiveEditorOpDialogForm {
  return {
    stagePlanDate: source.stagePlanDate,
    replyActual: source.replyActual,
    cause: source.cause,
    temp: source.temp,
    defectActual: source.defectActual,
    closeActual: source.closeActual,
  };
}

function formSignature(form: LiveEditorOpDialogForm): string {
  return JSON.stringify(form);
}

export default Vue.extend({
  name: 'LiveEditorOpStageDialog',
  props: {
    visible: { type: Boolean, default: false },
    mode: { type: String as () => LiveEditorOpDialogMode, required: true },
    planDefault: { type: String, default: '' },
    meta: {
      type: Object as () => LiveEditorOpDialogMeta,
      default: (): LiveEditorOpDialogMeta => ({}),
    },
    readonly: { type: Boolean, default: false },
    planReadonly: { type: Boolean, default: false },
    confirming: { type: Boolean, default: false },
    showCloseCase: { type: Boolean, default: false },
    initial: {
      type: Object as () => LiveEditorOpDialogForm,
      required: true,
    },
  },
  data() {
    return {
      form: cloneForm({
        stagePlanDate: null,
        replyActual: null,
        cause: '',
        temp: '',
        defectActual: null,
        closeActual: null,
      }),
      snapshot: '',
    };
  },
  computed: {
    visibleSync: {
      get(): boolean {
        return this.visible;
      },
      set(value: boolean) {
        this.$emit('update:visible', value);
      },
    },
    dialogTitle(): string {
      if (this.mode === 'reply') return '回复处理';
      if (this.mode === 'defect') return '不良品处理';
      return '结案计划';
    },
    modeTag(): string {
      if (this.mode === 'reply') return '回复';
      if (this.mode === 'defect') return '不良品';
      return '结案';
    },
    isDirty(): boolean {
      return formSignature(this.form) !== this.snapshot;
    },
    confirmDisabled(): boolean {
      if (this.confirming) return true;
      return (this.readonly && this.mode !== 'close') || (this.mode === 'close' && this.planReadonly);
    },
    displayMeta(): { date: string; yc: string; deptP: string; fzr: string; part: string } {
      const meta = this.meta as LiveEditorOpDialogMeta;
      const pm = String(meta.pm || '').trim();
      const lh = String(meta.lh || '').trim();
      const part = [pm, lh].filter(Boolean).join(' / ');
      return {
        date: String(meta.date || '').trim() || '—',
        yc: String(meta.yc || '').trim() || '—',
        deptP: String(meta.deptP || '').trim() || '—',
        fzr: String(meta.fzr || '').trim() || '—',
        part,
      };
    },
  },
  watch: {
    visible(open: boolean) {
      if (open) this.resetForm();
    },
  },
  methods: {
    resetForm() {
      this.form = cloneForm(this.initial);
      this.snapshot = formSignature(this.form);
    },
    onClosed() {
      this.$emit('close');
    },
    onConfirm() {
      if (this.confirmDisabled) return;
      this.$emit('confirm', cloneForm(this.form));
    },
    onCancel() {
      this.requestClose();
    },
    requestClose() {
      if (this.confirming) return;
      if (!this.isDirty) {
        this.visibleSync = false;
        return;
      }
      this.$confirm('已修改的内容尚未保存，确定关闭？', '提示', {
        type: 'warning',
        confirmButtonText: '关闭',
        cancelButtonText: '继续编辑',
      })
        .then(() => {
          this.visibleSync = false;
        })
        .catch(() => undefined);
    },
    beforeClose(done: () => void) {
      if (this.confirming) return;
      if (!this.isDirty) {
        done();
        return;
      }
      this.$confirm('已修改的内容尚未保存，确定关闭？', '提示', {
        type: 'warning',
        confirmButtonText: '关闭',
        cancelButtonText: '继续编辑',
      })
        .then(() => done())
        .catch(() => undefined);
    },
  },
});
</script>

<style lang="scss" scoped>
.live-editor-op-dialog__head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-right: 24px;
}

.live-editor-op-dialog__title {
  font-size: 16px;
  font-weight: 600;
  color: $color-text-regular;
}

.live-editor-op-dialog__tag {
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 500;
  color: $color-primary;
  background: rgba($color-primary, 0.08);
}

.live-editor-op-dialog__shell {
  min-height: 120px;
  padding: 14px 16px 10px;
  background: #f5f7fa;
}

.live-editor-op-dialog__block {
  margin-bottom: 14px;

  &:last-child {
    margin-bottom: 0;
  }
}

.live-editor-op-dialog__block-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 8px;
}

.live-editor-op-dialog__block-mark {
  flex-shrink: 0;
  width: 3px;
  height: 14px;
  border-radius: 2px;
  background: $color-primary;
}

.live-editor-op-dialog__block-title {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.3;
  color: $color-text-regular;
}

.live-editor-op-dialog__panel {
  border: 1px solid $color-hairline;
  border-radius: 6px;
  background: $color-surface;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);

  &--meta {
    padding: 4px 12px;
  }

  &--form {
    padding: 12px 12px 2px;
  }
}

.live-editor-op-dialog__meta-grid {
  display: flex;
  flex-direction: column;
}

.live-editor-op-dialog__meta-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 0;
  padding: 8px 0;
  border-bottom: 1px solid #f0f2f5;

  &:last-child {
    border-bottom: 0;
    padding-bottom: 4px;
  }

  &:first-child {
    padding-top: 4px;
  }
}

.live-editor-op-dialog__meta-label {
  flex: 0 0 64px;
  font-size: 12px;
  line-height: 1.4;
  color: $color-text-secondary;
  text-align: right;
}

.live-editor-op-dialog__meta-value {
  flex: 1;
  min-width: 0;
  font-size: 12px;
  line-height: 1.4;
  color: $color-text-regular;
  word-break: break-word;
}

.live-editor-op-dialog__date {
  width: 100%;
}

.live-editor-op-dialog__default {
  display: block;
  margin-top: 6px;
  padding: 0;
  border: 0;
  background: transparent;
  font-size: 11px;
  color: $color-primary;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}

.live-editor-op-dialog__readonly {
  font-size: 13px;
  color: $color-text-regular;
}

.live-editor-op-dialog__foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 12px;
}

.live-editor-op-dialog__foot-left,
.live-editor-op-dialog__foot-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.live-editor-op-dialog__foot-right {
  margin-left: auto;
}
</style>

<style lang="scss">
/* 相对全屏 wrapper 居中（覆盖 Element 默认 margin-top） */
.live-editor-op-dialog.el-dialog {
  position: absolute;
  top: 50%;
  left: 50%;
  margin: 0 !important;
  transform: translate(-50%, -50%);
  max-height: calc(100vh - 48px);
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  overflow: hidden;
}

.live-editor-op-dialog .el-dialog__body {
  flex: 1;
  min-height: 0;
  padding: 0;
  overflow: auto;
}

.live-editor-op-dialog .el-dialog__header {
  padding: 14px 16px 12px;
  border-bottom: 1px solid #ebeef5;
}


.live-editor-op-dialog .el-dialog__footer {
  padding: 10px 16px 14px;
  border-top: 1px solid #ebeef5;
}
</style>
